import { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FolderIcon from '@mui/icons-material/Folder'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { gql, useQuery } from '@apollo/client'
import { getIconByMimetype } from 'components/Datarooms/Stages/FileIcons'
import {
  Dataroom,
  DataroomFile,
  LinkedFile,
  Stage,
} from 'utils/__generated__/graphql'

export type LinkedFileInput = {
  dataroom: string
  dataroomName: string
  stage: string
  stageName: string
  file: string
  filename: string
  url: string
  mimetype: string
}

interface FilePickerDialogProps {
  open: boolean
  siteId: string
  alreadyLinked: LinkedFile[]
  onClose: () => void
  onConfirm: (files: LinkedFileInput[]) => void
}

type View = 'datarooms' | 'stages' | 'files'

const FIND_SITE_DATAROOMS_FOR_PICKER = gql`
  query FIND_SITE_DATAROOMS_FOR_PICKER($filter: JSON!) {
    datarooms: findDatarooms(filter: $filter) {
      id
      name
      stages {
        id
        name
        files {
          id
          filename
          url
          mimetype
          filesize
        }
      }
    }
  }
`

const buildLinkedFileInput = (
  dataroom: Dataroom,
  stage: Stage,
  file: DataroomFile
): LinkedFileInput => ({
  dataroom: dataroom.id ?? '',
  dataroomName: dataroom.name ?? '',
  stage: stage.id ?? '',
  stageName: stage.name ?? '',
  file: file.id ?? '',
  filename: file.filename ?? '',
  url: file.url ?? '',
  mimetype: file.mimetype ?? '',
})

function FilePickerDialog({
  open,
  siteId,
  alreadyLinked,
  onClose,
  onConfirm,
}: FilePickerDialogProps) {
  const [view, setView] = useState<View>('datarooms')
  const [selectedDataroom, setSelectedDataroom] = useState<Dataroom | null>(
    null
  )
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null)
  const [checked, setChecked] = useState<Map<string, LinkedFileInput>>(
    new Map()
  )

  const { data, loading } = useQuery(FIND_SITE_DATAROOMS_FOR_PICKER, {
    variables: { filter: { site: siteId } },
    skip: !open || !siteId,
  })

  const alreadyLinkedIds = useMemo(
    () => new Set(alreadyLinked.map(l => l.file).filter(Boolean)),
    [alreadyLinked]
  )

  const resetState = useCallback(() => {
    setView('datarooms')
    setSelectedDataroom(null)
    setSelectedStage(null)
    setChecked(new Map())
  }, [])

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleDone = () => {
    onConfirm(Array.from(checked.values()))
    resetState()
    onClose()
  }

  const handleDataroomClick = (dataroom: Dataroom) => {
    setSelectedDataroom(dataroom)
    setView('stages')
  }

  const handleStageClick = (stage: Stage) => {
    setSelectedStage(stage)
    setView('files')
  }

  const handleToggleFile = (file: DataroomFile) => {
    if (!selectedDataroom || !selectedStage || !file.id) return

    setChecked(prev => {
      const next = new Map(prev)

      if (next.has(file.id!)) {
        next.delete(file.id!)
      } else {
        next.set(
          file.id!,
          buildLinkedFileInput(selectedDataroom, selectedStage, file)
        )
      }

      return next
    })
  }

  const navigateTo = (target: View) => {
    setView(target)

    if (target === 'datarooms') {
      setSelectedDataroom(null)
      setSelectedStage(null)
    }

    if (target === 'stages') {
      setSelectedStage(null)
    }
  }

  const datarooms: Dataroom[] = data?.datarooms ?? []

  const title = {
    datarooms: 'Select Dataroom',
    stages: 'Select Stage',
    files: 'Select Files',
  }[view]

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { minHeight: 400 } }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {view !== 'datarooms' && (
            <IconButton
              size="small"
              onClick={() =>
                navigateTo(view === 'files' ? 'stages' : 'datarooms')
              }
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6">{title}</Typography>
        </Stack>

        {view !== 'datarooms' && (
          <Breadcrumbs sx={{ mt: 0.5 }}>
            <Link
              component="button"
              underline="hover"
              variant="body2"
              onClick={() => navigateTo('datarooms')}
            >
              Datarooms
            </Link>

            {view === 'stages' && (
              <Typography variant="body2" color="text.primary">
                {selectedDataroom?.name}
              </Typography>
            )}

            {view === 'files' && (
              <Link
                component="button"
                underline="hover"
                variant="body2"
                onClick={() => navigateTo('stages')}
              >
                {selectedDataroom?.name}
              </Link>
            )}

            {view === 'files' && (
              <Typography variant="body2" color="text.primary">
                {selectedStage?.name}
              </Typography>
            )}
          </Breadcrumbs>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        )}

        {!loading && view === 'datarooms' && (
          <DataroomsList datarooms={datarooms} onClick={handleDataroomClick} />
        )}

        {!loading && view === 'stages' && selectedDataroom && (
          <StagesList
            stages={selectedDataroom.stages ?? []}
            onClick={handleStageClick}
          />
        )}

        {!loading && view === 'files' && selectedStage && (
          <FilesList
            files={selectedStage.files ?? []}
            checked={checked}
            alreadyLinkedIds={alreadyLinkedIds}
            onToggle={handleToggleFile}
          />
        )}
      </DialogContent>

      <Divider />

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>

        {view === 'files' && (
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

function DataroomsList({
  datarooms,
  onClick,
}: {
  datarooms: Dataroom[]
  onClick: (d: Dataroom) => void
}) {
  if (datarooms.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No datarooms found</Typography>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {datarooms.map(dataroom => {
        const stageCount = dataroom.stages?.length ?? 0

        return (
          <ListItem key={dataroom.id} disablePadding divider>
            <ListItemButton onClick={() => onClick(dataroom)}>
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText
                primary={dataroom.name}
                secondary={`${stageCount} stage${stageCount !== 1 ? 's' : ''}`}
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        )
      })}
    </List>
  )
}

function StagesList({
  stages,
  onClick,
}: {
  stages: Array<Stage | null>
  onClick: (s: Stage) => void
}) {
  const validStages = stages.filter(Boolean) as Stage[]

  if (validStages.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No stages found</Typography>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {validStages.map(stage => {
        const fileCount = stage.files?.length ?? 0

        return (
          <ListItem key={stage.id} disablePadding divider>
            <ListItemButton onClick={() => onClick(stage)}>
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText
                primary={stage.name}
                secondary={`${fileCount} file${fileCount !== 1 ? 's' : ''}`}
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        )
      })}
    </List>
  )
}

function FilesList({
  files,
  checked,
  alreadyLinkedIds,
  onToggle,
}: {
  files: Array<DataroomFile | null>
  checked: Map<string, LinkedFileInput>
  alreadyLinkedIds: Set<string | null | undefined>
  onToggle: (f: DataroomFile) => void
}) {
  const validFiles = files.filter(Boolean) as DataroomFile[]

  if (validFiles.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No files found</Typography>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {validFiles.map(file => {
        const fileId = file.id ?? ''
        const linked = alreadyLinkedIds.has(fileId)
        const isChecked = linked || checked.has(fileId)

        return (
          <ListItem key={fileId} disablePadding divider>
            <ListItemButton
              onClick={() => !linked && onToggle(file)}
              disabled={linked}
              dense
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={isChecked}
                  disabled={linked}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {getIconByMimetype(file.mimetype ?? '')}
              </ListItemIcon>
              <ListItemText
                primary={file.filename}
                secondary={linked ? 'Already linked' : file.mimetype}
              />
            </ListItemButton>
          </ListItem>
        )
      })}
    </List>
  )
}

export default FilePickerDialog
